import React, {useReducer, type PropsWithChildren} from 'react';
import {Link, useParams} from 'react-router-dom';
import {useGetOrganizations} from 'api/generated';
import {Dialog, DialogBackdrop, DialogPanel, TransitionChild} from '@headlessui/react';
import {ButtonTheme, Transitioner} from 'components';
import {imgBrand} from 'assets';
import {classNames} from 'utils';
import {useMe} from 'hooks';
import {
  BanknotesIcon,
  Bars3Icon,
  BookOpenIcon,
  // ChartPieIcon,
  Cog6ToothIcon,
  CubeIcon,
  FolderIcon,
  HomeIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const NAVIGATIONS = [
  {name: 'Home', path: 'home', icon: HomeIcon},
  {name: 'Projects', path: 'projects', icon: FolderIcon},
  {name: 'Assets', path: 'assets', icon: CubeIcon},
  {name: 'Team', path: 'team', icon: UsersIcon},
  // {name: 'Analytics', path: 'analytics', icon: ChartPieIcon},
  {name: 'Help', path: 'help', icon: BookOpenIcon},
  {name: 'Billing', path: 'billing', icon: BanknotesIcon},
  {name: 'Settings', path: 'settings', icon: Cog6ToothIcon},
];

interface ILayoutProps extends PropsWithChildren {
  hidden?: boolean;
}

const Layout = ({children, hidden}: ILayoutProps) => {
  const [sidebarOpen, toggleSidebar] = useReducer(s => !s, false);

  const params = useParams();
  const workspace = params.workspace;
  const resource = params.resource;

  const me = useMe();
  const {data: orgData} = useGetOrganizations({query: {enabled: !hidden && !!me}});
  const organizations = orgData?.data?.data ?? [];

  const myWorkspace: (typeof organizations)[0] = {
    type: 'organization',
    uid: 'me',
    name: me?.name ?? '',
    slug: 'me',
  };
  const workspaces = [myWorkspace, ...organizations];

  if (!me) {
    return <div className="w-screen h-screen"></div>;
  }

  if (hidden) {
    return children;
  }

  return (
    <div>
      {/* Collapsible sidebar for mobile */}
      <Dialog open={sidebarOpen} onClose={toggleSidebar} className="relative z-50 lg:hidden">
        <DialogBackdrop transition className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0" />
        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full">
            <TransitionChild>
              <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                <button type="button" onClick={toggleSidebar} className="-m-2.5 p-2.5 cursor-pointer">
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon aria-hidden="true" className="size-6 text-white active:scale-90" />
                </button>
              </div>
            </TransitionChild>
            {/* Sidebar component, swap this element with another sidebar if you like */}
            <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-neutral-950 px-6 pb-2">
              <div className="flex h-16 shrink-0 items-center justify-between">
                <img alt="DasAR" src={imgBrand} className="h-8 w-auto" />
                <ButtonTheme />
              </div>
              <nav className="flex flex-1 flex-col">
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="-mx-2 space-y-1">
                      {NAVIGATIONS.map(item => {
                        return <NavItem key={item.name} workspace={workspace} resource={resource} item={item} onClick={toggleSidebar} />;
                      })}
                    </ul>
                  </li>
                  <li>
                    <div className="text-xs/6 font-semibold text-gray-400">My workspaces</div>
                    <ul role="list" className="-mx-2 mt-2 space-y-1">
                      {workspaces.map(space => {
                        const current = space.slug === workspace;
                        return (
                          <li key={space.uid}>
                            <Link
                              to={location.pathname.replace(`/${workspace}/`, `/${space.slug}/`)}
                              className={classNames(
                                current
                                  ? 'bg-gray-100 dark:bg-darkBg text-primary'
                                  : 'text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-darkBg hover:text-primary',
                                'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                              )}>
                              <div
                                className={classNames(
                                  current
                                    ? 'border-primary text-primary'
                                    : 'border-gray-200 text-gray-400 group-hover:border-primary group-hover:text-primary',
                                  'flex size-6 shrink-0 items-center justify-center rounded-lg border bg-white dark:bg-darkBg text-[0.625rem] font-medium',
                                )}>
                                <CubeIcon className="size-4" />
                              </div>
                              <span className="truncate">{space.name}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Static sidebar for desktop */}
      <section className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-54 lg:flex-col">
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-neutral-950 px-6">
          <Transitioner fromX={-50} fromY={0}>
            <div className="flex h-16 shrink-0 items-center justify-between">
              <img alt="DasAR" src={imgBrand} className="h-10 w-auto" />
              <ButtonTheme />
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {NAVIGATIONS.map(item => {
                      return <NavItem key={item.path} workspace={workspace} resource={resource} item={item} />;
                    })}
                  </ul>
                </li>
                <li>
                  <div className="text-xs/6 font-semibold text-gray-400">My workspaces</div>
                  <ul role="list" className="-mx-2 mt-2 space-y-1">
                    {workspaces.map(space => {
                      const current = space.slug === workspace;
                      return (
                        <li key={space.uid}>
                          <Link
                            to={location.pathname.replace(`/${workspace}/`, `/${space.slug}/`)}
                            className={classNames(
                              current
                                ? 'bg-gray-100 dark:bg-darkBg text-primary'
                                : 'text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-darkBg hover:text-primary',
                              'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                            )}>
                            <div
                              className={classNames(
                                current
                                  ? 'border-primary text-primary'
                                  : 'border-gray-200 text-gray-400 group-hover:border-primary group-hover:text-primary',
                                'flex size-6 shrink-0 items-center justify-center rounded-lg border bg-white dark:bg-darkBg text-[0.625rem] font-medium',
                              )}>
                              <CubeIcon className="size-4" />
                            </div>
                            <span className="truncate">{space.name}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              </ul>
            </nav>
          </Transitioner>
        </div>
      </section>

      {/* Topbar for small to mid size */}
      <section className="sticky top-0 z-40 flex items-center gap-x-6 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-neutral-950 px-4 py-4 shadow-xs sm:px-6 lg:hidden">
        <button
          type="button"
          onClick={toggleSidebar}
          className="-m-2.5 p-2.5 text-gray-700 dark:text-white dark:hover:text-gray-400 duration-300 lg:hidden active:scale-90 cursor-pointer">
          <span className="sr-only">Open sidebar</span>
          <Bars3Icon aria-hidden="true" className="size-6" />
        </button>
        <div className="flex-1 text-sm/6 font-semibold text-gray-900 dark:text-white capitalize">{resource}</div>
      </section>

      <section className="py-5 lg:pl-54 min-h-screen">
        <div className="px-4 sm:px-6 lg:px-8">{children}</div>
      </section>
    </div>
  );
};

export default Layout;

interface INavItemProps {
  workspace: string | undefined;
  resource: string | undefined;
  item: (typeof NAVIGATIONS)[0];
  onClick?: () => void;
}

const NavItem = React.memo(({workspace, resource, item, onClick}: INavItemProps) => {
  const current = resource === item.path;
  return (
    <li>
      <Link
        to={`/${workspace}/${item.path}`}
        onClick={() => onClick?.()}
        className={classNames(
          current
            ? 'bg-gray-100 dark:bg-darkBg text-primary'
            : 'text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-darkBg hover:text-primary',
          'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
        )}>
        <item.icon
          aria-hidden="true"
          className={classNames(current ? 'text-primary' : 'text-gray-400 group-hover:text-primary', 'size-6 shrink-0')}
        />
        {item.name}
      </Link>
    </li>
  );
});
